// T060: Effect Trigger System
// 統一管理所有效果觸發點

import type {
  GameState,
  Card,
  CardDefinition,
  BattlefieldPosition,
  StateChange,
  EffectTrigger,
} from '../../types';
import { ActionExecutor, createActionExecutor } from './ActionExecutor';

// 匯入所有效果處理器
import {
  // 基礎處理器
  DestroyEffectHandler,
  // ReturnToHandEffectHandler → 使用 V2
  TapEffectHandler,
  SummonTravelerEffectHandler,
  // SearchDeckEffectHandler → 使用 V2
  AttackBuffEffectHandler,
  ExtraPlayEffectHandler,
  // DiscardEffectHandler → 使用 V2
  // 關鍵字處理器
  SneakAttackHandler,
  PoisonHandler,
  FlyingHandler,
  PiercingHandler,
  RangedAttackHandler,
  BlockHandler,
  ArmorBreakHandler,
  SlideHandler,
  ImprisonHandler,
  CoverHandler,
  VanishHandler,
  DecayHandler,
  DismantleHandler,
  // 資源處理器
  ChargeHandler,
  ConsumeEnergyHandler,
  RewardEffectHandlerV2,
  DrawEffectHandlerV2,
  DiscardEffectHandlerV2,
  // 牌庫處理器
  RevealTopHandler,
  SearchDeckHandlerV2,
  ReturnToDeckHandler,
  ShuffleHandler,
  // 戰場處理器
  MoveHandler,
  KidnapHandler,
  SummonToPositionHandler,
  OrientationHandler,
  ExileHandler,
  GraveyardSummonHandler,
  ReturnToHandHandlerV2,
  // 戰鬥處理器
  CombatSuccessHandler,
  CombatDestroyHandler,
  AttackDefendHandler,
  AttackModifierHandler,
  ResistanceHandler,
  NullifyHandler,
  // 特殊處理器
  UpgradeHandler,
  QuantityLimitHandler,
  ExtraZoneHandler,
  HuCardHandler,
  FieldMagicHandler,
  SacrificeHandler,
  ItemHandler,
  MerchantHandler,
  TurnLimitHandler,
  ConditionalHandler,
  // 法術處理器
  DamageHandler,
  ReturnToHandHandler,
  ShuffleToDeckHandler,
  AttackRedirectHandler,
  DestroyTargetHandler,
  ModifyAtkHandler,
  TapTargetHandler,
  UntapHandler,
  GrantKeywordHandler,
  GraveyardToHandHandler,
  GraveyardSummonSpellHandler,
  NullifyAttackHandler,
  DestroyFieldHandler,
  TransformHandler,
  // 合體處理器
  MechaMergeHandler,
  DevourMergeHandler,
  ParasiteMergeHandler,
  SeparateMergeHandler,
  // 搭乘處理器
  BoardHandler,
  DisembarkHandler,
  EmbarkHandler,
  SummonTravelerToForestHandler,
  // 亡魂處理器
  SoulHandler,
  ReviveHandler,
  SoulSacrificeHandler,
  SoulBuffHandler,
  // 通電處理器
  ElectrifyHandler,
} from './handlers';
import { getLogger } from '../../utils/logger';

const log = getLogger('EffectTriggerSystem');

/**
 * 觸發事件資料
 */
export interface TriggerEvent {
  type: EffectTrigger;
  sourceCard: Card;
  sourcePosition?: BattlefieldPosition;
  targetCard?: Card;
  targetPosition?: BattlefieldPosition;
  additionalData?: Record<string, unknown>;
}

/**
 * 觸發結果
 */
export interface TriggerResult {
  stateChanges: StateChange[];
  immediateActions: ImmediateAction[];
  messages: string[];
}

/**
 * 立即行動（需要進一步處理）
 */
export interface ImmediateAction {
  type: 'summon' | 'destroy' | 'draw' | 'discard' | 'move' | 'buff' | 'debuff' | 'custom';
  sourceCardId: string;
  targetSelector?: TargetSelector;
  value?: number | string;
  customHandler?: string;

  // 選擇系統支援
  targetCardId?: string;           // 單一目標卡牌 ID
  targetCardIds?: string[];        // 多個目標卡牌 ID
  targetPosition?: BattlefieldPosition;  // 目標位置
  choiceRequest?: unknown;         // 選擇請求（由 ChoiceSystem 定義）
  requiresChoice?: boolean;        // 是否需要玩家選擇

  // 條件分支
  condition?: unknown;             // 條件判斷
  consequent?: ImmediateAction[];  // 條件成立時執行的動作
}

/**
 * 目標選擇器
 */
export interface TargetSelector {
  zone: 'battlefield' | 'hand' | 'graveyard' | 'deck' | 'forest' | 'extra';
  side: 'self' | 'enemy' | 'both' | 'any';
  filter?: {
    faction?: string;
    type?: 'unit' | 'spell';
    name?: string;
    minAtk?: number;
    maxAtk?: number;
  };
  count?: number;
  random?: boolean;
}

/**
 * 效果處理器介面
 */
export interface EffectHandler {
  canHandle(effectText: string): boolean;
  parse(effectText: string): ImmediateAction[];
  execute(state: GameState, action: ImmediateAction): GameState;
}

/**
 * 效果觸發系統
 */
export class EffectTriggerSystem {
  private handlers: EffectHandler[] = [];
  private definitions: Map<string, CardDefinition> = new Map();
  private actionExecutor: ActionExecutor | null = null;

  constructor(definitions?: Map<string, CardDefinition>) {
    if (definitions) {
      this.definitions = definitions;
      this.actionExecutor = createActionExecutor(definitions);
    }
    this.registerDefaultHandlers();
  }

  /**
   * 設定卡牌定義
   */
  setDefinitions(definitions: Map<string, CardDefinition>): void {
    this.definitions = definitions;
    this.actionExecutor = createActionExecutor(definitions);
  }

  /**
   * 註冊效果處理器
   */
  registerHandler(handler: EffectHandler): void {
    this.handlers.push(handler);
  }

  /**
   * 註冊預設處理器
   */
  private registerDefaultHandlers(): void {
    // ========================================
    // 基礎處理器
    // ========================================
    this.registerHandler(new SummonEffectHandler());
    this.registerHandler(new BuffEffectHandler());
    // RewardEffectHandler → 使用 V2 (在資源處理器區塊)
    // DrawEffectHandler → 使用 V2 (在資源處理器區塊)
    
    // 基礎效果處理器
    this.registerHandler(new DestroyEffectHandler());
    // ReturnToHandEffectHandler → 使用 V2 (在戰場處理器區塊)
    this.registerHandler(new TapEffectHandler());
    this.registerHandler(new SummonTravelerEffectHandler());
    // SearchDeckEffectHandler → 使用 V2 (在牌庫處理器區塊)
    this.registerHandler(new AttackBuffEffectHandler());
    this.registerHandler(new ExtraPlayEffectHandler());
    // DiscardEffectHandler → 使用 V2 (在資源處理器區塊)

    // ========================================
    // 關鍵字處理器（喵喵賊等）
    // ========================================
    this.registerHandler(new SneakAttackHandler());
    this.registerHandler(new PoisonHandler());
    this.registerHandler(new FlyingHandler());
    this.registerHandler(new PiercingHandler());
    this.registerHandler(new RangedAttackHandler());
    this.registerHandler(new BlockHandler());
    this.registerHandler(new ArmorBreakHandler());
    this.registerHandler(new SlideHandler());
    this.registerHandler(new ImprisonHandler());
    this.registerHandler(new CoverHandler());
    this.registerHandler(new VanishHandler());
    this.registerHandler(new DecayHandler());
    this.registerHandler(new DismantleHandler());

    // ========================================
    // 資源處理器（蒸汽世界等）
    // ========================================
    this.registerHandler(new ChargeHandler());
    this.registerHandler(new ConsumeEnergyHandler());
    this.registerHandler(new RewardEffectHandlerV2());  // 獎勵效果 (V2)
    this.registerHandler(new DrawEffectHandlerV2());    // 抽牌效果 (V2)
    this.registerHandler(new DiscardEffectHandlerV2()); // 棄牌效果 (V2)

    // ========================================
    // 牌庫處理器（歡樂島等）
    // ========================================
    this.registerHandler(new RevealTopHandler());
    this.registerHandler(new SearchDeckHandlerV2());    // 搜尋牌庫 (V2)
    this.registerHandler(new ReturnToDeckHandler());
    this.registerHandler(new ShuffleHandler());

    // ========================================
    // 戰場處理器
    // ========================================
    this.registerHandler(new MoveHandler());
    this.registerHandler(new KidnapHandler());
    this.registerHandler(new SummonToPositionHandler());
    this.registerHandler(new OrientationHandler());
    this.registerHandler(new ExileHandler());
    this.registerHandler(new GraveyardSummonHandler());
    this.registerHandler(new ReturnToHandHandlerV2());

    // ========================================
    // 戰鬥處理器
    // ========================================
    this.registerHandler(new CombatSuccessHandler());
    this.registerHandler(new CombatDestroyHandler());
    this.registerHandler(new AttackDefendHandler());
    this.registerHandler(new AttackModifierHandler());
    this.registerHandler(new ResistanceHandler());
    this.registerHandler(new NullifyHandler());

    // ========================================
    // 特殊處理器（勇者公會升級等）
    // ========================================
    this.registerHandler(new UpgradeHandler());
    this.registerHandler(new QuantityLimitHandler());
    this.registerHandler(new ExtraZoneHandler());
    this.registerHandler(new HuCardHandler());
    this.registerHandler(new FieldMagicHandler());
    this.registerHandler(new SacrificeHandler());
    this.registerHandler(new ItemHandler());
    this.registerHandler(new MerchantHandler());
    this.registerHandler(new TurnLimitHandler());
    this.registerHandler(new ConditionalHandler());

    // ========================================
    // 法術效果處理器
    // ========================================
    this.registerHandler(new DamageHandler());
    this.registerHandler(new ReturnToHandHandler());
    this.registerHandler(new ShuffleToDeckHandler());
    this.registerHandler(new AttackRedirectHandler());
    this.registerHandler(new DestroyTargetHandler());
    this.registerHandler(new ModifyAtkHandler());
    this.registerHandler(new TapTargetHandler());
    this.registerHandler(new UntapHandler());
    this.registerHandler(new GrantKeywordHandler());
    this.registerHandler(new GraveyardToHandHandler());
    this.registerHandler(new GraveyardSummonSpellHandler());
    this.registerHandler(new NullifyAttackHandler());
    this.registerHandler(new DestroyFieldHandler());
    this.registerHandler(new TransformHandler());

    // ========================================
    // 合體效果處理器
    // ========================================
    this.registerHandler(new MechaMergeHandler());
    this.registerHandler(new DevourMergeHandler());
    this.registerHandler(new ParasiteMergeHandler());
    this.registerHandler(new SeparateMergeHandler());

    // ========================================
    // 搭乘效果處理器
    // ========================================
    this.registerHandler(new BoardHandler());
    this.registerHandler(new DisembarkHandler());
    this.registerHandler(new EmbarkHandler());
    this.registerHandler(new SummonTravelerToForestHandler());

    // ========================================
    // 亡魂效果處理器
    // ========================================
    this.registerHandler(new SoulHandler());
    this.registerHandler(new ReviveHandler());
    this.registerHandler(new SoulSacrificeHandler());
    this.registerHandler(new SoulBuffHandler());

    // ========================================
    // 通電效果處理器
    // ========================================
    this.registerHandler(new ElectrifyHandler());
  }

  /**
   * 觸發召喚時效果
   */
  triggerOnSummon(
    state: GameState,
    card: Card,
    position: BattlefieldPosition
  ): TriggerResult {
    return this.processTrigger(state, {
      type: 'onSummon',
      sourceCard: card,
      sourcePosition: position,
    });
  }

  /**
   * 觸發並執行召喚時效果
   */
  triggerAndExecuteOnSummon(
    state: GameState,
    card: Card,
    position: BattlefieldPosition
  ): { state: GameState; messages: string[] } {
    return this.triggerAndExecute(state, {
      type: 'onSummon',
      sourceCard: card,
      sourcePosition: position,
    });
  }

  /**
   * 觸發死亡時效果
   */
  triggerOnDestroy(
    state: GameState,
    card: Card,
    position: BattlefieldPosition,
    destroyer?: Card
  ): TriggerResult {
    return this.processTrigger(state, {
      type: 'onDestroy',
      sourceCard: card,
      sourcePosition: position,
      targetCard: destroyer,
    });
  }

  /**
   * 觸發並執行死亡時效果
   */
  triggerAndExecuteOnDestroy(
    state: GameState,
    card: Card,
    position: BattlefieldPosition,
    destroyer?: Card
  ): { state: GameState; messages: string[] } {
    return this.triggerAndExecute(state, {
      type: 'onDestroy',
      sourceCard: card,
      sourcePosition: position,
      targetCard: destroyer,
    });
  }

  /**
   * 觸發戰鬥成功時效果
   */
  triggerOnCombatSuccess(
    state: GameState,
    winner: Card,
    winnerPosition: BattlefieldPosition,
    loser: Card
  ): TriggerResult {
    return this.processTrigger(state, {
      type: 'onKill',
      sourceCard: winner,
      sourcePosition: winnerPosition,
      targetCard: loser,
    });
  }

  /**
   * 觸發並執行戰鬥成功時效果
   */
  triggerAndExecuteOnCombatSuccess(
    state: GameState,
    winner: Card,
    winnerPosition: BattlefieldPosition,
    loser: Card
  ): { state: GameState; messages: string[] } {
    return this.triggerAndExecute(state, {
      type: 'onKill',
      sourceCard: winner,
      sourcePosition: winnerPosition,
      targetCard: loser,
    });
  }

  /**
   * 觸發進攻時效果
   */
  triggerOnAttack(
    state: GameState,
    attacker: Card,
    attackerPosition: BattlefieldPosition,
    defender?: Card
  ): TriggerResult {
    return this.processTrigger(state, {
      type: 'onAttack',
      sourceCard: attacker,
      sourcePosition: attackerPosition,
      targetCard: defender,
    });
  }

  /**
   * 觸發並執行進攻時效果
   */
  triggerAndExecuteOnAttack(
    state: GameState,
    attacker: Card,
    attackerPosition: BattlefieldPosition,
    defender?: Card
  ): { state: GameState; messages: string[] } {
    return this.triggerAndExecute(state, {
      type: 'onAttack',
      sourceCard: attacker,
      sourcePosition: attackerPosition,
      targetCard: defender,
    });
  }

  /**
   * 觸發回合開始效果
   */
  triggerOnTurnStart(
    state: GameState,
    playerId: string
  ): TriggerResult {
    // 觸發場上所有該玩家單位的回合開始效果
    const result: TriggerResult = {
      stateChanges: [],
      immediateActions: [],
      messages: [],
    };

    const isPlayer1 = playerId === state.player1.id;
    const side = isPlayer1 ? state.battlefield.player1Side : state.battlefield.player2Side;
    
    const processLine = (line: typeof side.frontLine, row: 'front' | 'back') => {
      line.forEach((slot, idx) => {
        if (slot.unit) {
          const unitResult = this.processTrigger(state, {
            type: 'onTurnStart',
            sourceCard: slot.unit,
            sourcePosition: {
              side: isPlayer1 ? 'player1' : 'player2',
              row,
              column: (idx + 1) as 1 | 2 | 3 | 4 | 5,
            },
          });
          result.immediateActions.push(...unitResult.immediateActions);
          result.messages.push(...unitResult.messages);
        }
      });
    };

    processLine(side.frontLine, 'front');
    processLine(side.backLine, 'back');

    return result;
  }

  /**
   * 觸發並執行回合開始效果
   */
  triggerAndExecuteOnTurnStart(
    state: GameState,
    playerId: string
  ): { state: GameState; messages: string[] } {
    const result = this.triggerOnTurnStart(state, playerId);
    
    if (result.immediateActions.length === 0) {
      return { state, messages: result.messages };
    }

    const newState = this.executeActions(state, result.immediateActions);
    return { state: newState, messages: result.messages };
  }

  /**
   * 觸發回合結束效果
   */
  triggerOnTurnEnd(
    state: GameState,
    playerId: string
  ): TriggerResult {
    const result: TriggerResult = {
      stateChanges: [],
      immediateActions: [],
      messages: [],
    };

    const isPlayer1 = playerId === state.player1.id;
    const side = isPlayer1 ? state.battlefield.player1Side : state.battlefield.player2Side;
    
    const processLine = (line: typeof side.frontLine, row: 'front' | 'back') => {
      line.forEach((slot, idx) => {
        if (slot.unit) {
          const unitResult = this.processTrigger(state, {
            type: 'onTurnEnd',
            sourceCard: slot.unit,
            sourcePosition: {
              side: isPlayer1 ? 'player1' : 'player2',
              row,
              column: (idx + 1) as 1 | 2 | 3 | 4 | 5,
            },
          });
          result.immediateActions.push(...unitResult.immediateActions);
          result.messages.push(...unitResult.messages);
        }
      });
    };

    processLine(side.frontLine, 'front');
    processLine(side.backLine, 'back');

    return result;
  }

  /**
   * 觸發並執行回合結束效果
   */
  triggerAndExecuteOnTurnEnd(
    state: GameState,
    playerId: string
  ): { state: GameState; messages: string[] } {
    const result = this.triggerOnTurnEnd(state, playerId);
    
    if (result.immediateActions.length === 0) {
      return { state, messages: result.messages };
    }

    const newState = this.executeActions(state, result.immediateActions);
    return { state: newState, messages: result.messages };
  }

  /**
   * 處理觸發事件
   */
  private processTrigger(_state: GameState, event: TriggerEvent): TriggerResult {
    const result: TriggerResult = {
      stateChanges: [],
      immediateActions: [],
      messages: [],
    };

    log.debug('Processing trigger: ' + event.type + ' for card: ' + event.sourceCard.definitionId);
    log.debug('Definitions size: ' + String(this.definitions.size));

    // 取得卡牌的效果文字
    const definition = this.definitions.get(event.sourceCard.definitionId);
    const effectText = definition?.effectText;
    
    log.debug(`Card name: ${definition?.name || 'unknown'}, effectText: ${effectText?.substring(0, 50) || 'none'}`);

    if (!effectText) {
      return result;
    }

    // 檢查是否有對應觸發條件
    const triggerPatterns = this.getTriggerPatterns(event.type);
    let shouldTrigger = false;

    // 法術和場地魔法直接觸發效果（不檢查觸發詞）
    if (event.type === 'onSpellCast' || event.type === 'onFieldEnter') {
      shouldTrigger = true;
    } else {
      for (const pattern of triggerPatterns) {
        if (effectText.includes(pattern)) {
          shouldTrigger = true;
          break;
        }
      }

      // 「立即」效果在召喚時觸發
      if (event.type === 'onSummon' && effectText.includes('立即')) {
        shouldTrigger = true;
      }
    }

    if (!shouldTrigger) {
      log.debug(`Effect NOT triggered - no matching pattern found`);
      return result;
    }

    log.debug(`Effect WILL trigger!`);

    // 解析並收集立即行動
    log.debug('Checking ' + String(this.handlers.length) + ' handlers');
    for (const handler of this.handlers) {
      const canHandle = handler.canHandle(effectText);
      log.debug('Handler ' + handler.constructor.name + ': canHandle = ' + String(canHandle));
      if (canHandle) {
        const actions = handler.parse(effectText);
        log.debug('Handler ' + handler.constructor.name + ' parsed ' + String(actions.length) + ' actions');
        result.immediateActions.push(...actions);
      }
    }

    result.messages.push(`${event.sourceCard.id} 觸發了 ${event.type} 效果`);

    return result;
  }

  /**
   * 取得觸發模式
   */
  private getTriggerPatterns(trigger: EffectTrigger): string[] {
    const patterns: Record<string, string[]> = {
      onSummon: ['召喚時', '入場時', '被召喚時', '打出時'],
      onDestroy: ['被破壞時', '死亡時', '被消滅時'],
      onKill: ['戰鬥成功', '擊敗', '消滅敵方'],
      onAttack: ['攻擊時', '進攻時'],
      onDefend: ['被攻擊時', '防禦時'],
      onTurnStart: ['回合開始'],
      onTurnEnd: ['回合結束'],
      // 法術和場地魔法直接觸發，不需要特定文字模式
      onSpellCast: [],
      onFieldEnter: [],
    };
    return patterns[trigger] ?? [];
  }

  /**
   * 執行立即行動（使用 ActionExecutor）
   */
  executeActions(
    state: GameState,
    actions: ImmediateAction[],
    context?: { sourceCardId?: string; sourcePosition?: BattlefieldPosition }
  ): GameState {
    if (!this.actionExecutor) {
      log.warn('ActionExecutor not initialized');
      return state;
    }

    const result = this.actionExecutor.executeAll(state, actions, context);
    
    if (result.messages.length > 0) {
      log.debug('Executed actions:', result.messages.join(', '));
    }
    
    return result.state;
  }

  /**
   * 觸發並執行效果（一站式方法）
   */
  triggerAndExecute(
    state: GameState,
    event: TriggerEvent
  ): { state: GameState; messages: string[] } {
    const result = this.processTrigger(state, event);
    
    if (result.immediateActions.length === 0) {
      return { state, messages: result.messages };
    }

    const context = {
      sourceCardId: event.sourceCard.id,
      sourcePosition: event.sourcePosition,
    };

    const newState = this.executeActions(state, result.immediateActions, context);
    
    return {
      state: newState,
      messages: result.messages,
    };
  }
}

/**
 * 召喚效果處理器
 * 處理「使X被召喚」類型的效果
 */
class SummonEffectHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('被召喚') || text.includes('召喚') || text === 'summon';
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使你額外區1張「XXX」被召喚」
    const extraSummonMatch = effectText.match(/使你額外區(\d+)張「([^」]+)」被召喚/);
    if (extraSummonMatch) {
      actions.push({
        type: 'summon',
        sourceCardId: '',
        targetSelector: {
          zone: 'extra',
          side: 'self',
          filter: { name: extraSummonMatch[2] },
          count: parseInt(extraSummonMatch[1], 10),
        },
      });
    }

    // 解析「召喚X個小旅人」
    const travelerMatch = effectText.match(/召喚(\d+)個小旅人/);
    if (travelerMatch) {
      actions.push({
        type: 'summon',
        sourceCardId: '',
        targetSelector: {
          zone: 'extra',
          side: 'self',
          filter: { name: '小旅人' },
          count: parseInt(travelerMatch[1], 10),
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    // 實作召喚邏輯
    const selector = action.targetSelector;
    if (!selector) return state;

    const count = selector.count ?? 1;
    const cardName = selector.filter?.name;

    // 找出召喚來源的玩家
    // 目前簡化：從 activePlayerId 判斷
    const playerId = state.activePlayerId;
    const isPlayer1 = playerId === state.player1.id;

    // 尋找空位
    const side = isPlayer1 ? state.battlefield.player1Side : state.battlefield.player2Side;
    const emptySlots: { row: 'front' | 'back'; column: number }[] = [];

    // 優先前排
    side.frontLine.forEach((slot, idx) => {
      if (!slot.unit) {
        emptySlots.push({ row: 'front', column: idx + 1 });
      }
    });
    // 再後排
    side.backLine.forEach((slot, idx) => {
      if (!slot.unit) {
        emptySlots.push({ row: 'back', column: idx + 1 });
      }
    });

    // 召喚單位到空位
    let newState = state;
    const summonsToPlace = Math.min(count, emptySlots.length);

    for (let i = 0; i < summonsToPlace; i++) {
      const slot = emptySlots[i];
      const newCard: import('../../types').Card = {
        id: `summoned-${cardName ?? 'unknown'}-${String(Date.now())}-${String(i)}`,
        definitionId: cardName === '小旅人' ? 'SPT-0001' : `summoned-${cardName ?? 'unknown'}`,
        ownerId: playerId,
        location: 'battlefield',
        position: {
          side: isPlayer1 ? 'player1' : 'player2',
          row: slot.row,
          column: slot.column as 1 | 2 | 3 | 4 | 5,
        },
        orientation: 'upright',
        summonedThisTurn: true,
      };

      // 放置單位
      const sideKey = isPlayer1 ? 'player1Side' : 'player2Side';
      const lineKey = slot.row === 'front' ? 'frontLine' : 'backLine';

      const newBattlefield = { ...newState.battlefield };
      const newSide = { ...newBattlefield[sideKey] };
      const newLine = [...newSide[lineKey]];
      newLine[slot.column - 1] = { ...newLine[slot.column - 1], unit: newCard };
      newSide[lineKey] = newLine;
      newBattlefield[sideKey] = newSide;

      newState = { ...newState, battlefield: newBattlefield };
    }

    return newState;
  }
}

/**
 * Buff 效果處理器
 * 處理「被賦予+X攻擊力」類型的效果
 */
class BuffEffectHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return (text.includes('被賦予') && text.includes('攻擊力')) || text === 'buff';
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「被賦予+X攻擊力」
    const buffMatch = effectText.match(/被賦予\+(\d+)攻擊力/);
    if (buffMatch) {
      actions.push({
        type: 'buff',
        sourceCardId: '',
        value: parseInt(buffMatch[1], 10),
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    // 實作 buff 邏輯
    const buffValue = typeof action.value === 'number' ? action.value : 0;
    if (buffValue === 0) return state;

    // 找出目標（目前預設是給自己加 buff）
    // 需要從 action.sourceCardId 找到卡牌位置
    const sourceId = action.sourceCardId;
    if (!sourceId) return state;

    // 搜尋並 buff 單位
    const buffUnit = (
      side: import('../../types').BattleSide,
      line: 'frontLine' | 'backLine'
    ): import('../../types').BattlefieldSlot[] | null => {
      const slots = side[line];
      const targetIdx = slots.findIndex((slot) => slot.unit?.id === sourceId);
      if (targetIdx === -1) return null;

      const newSlots = [...slots];
      const targetSlot = slots[targetIdx];
      if (targetSlot.unit) {
        newSlots[targetIdx] = {
          ...targetSlot,
          unit: {
            ...targetSlot.unit,
            atkModifier: (targetSlot.unit.atkModifier ?? 0) + buffValue,
          },
        };
      }
      return newSlots;
    };

    // 嘗試 player1Side
    const p1Front = buffUnit(state.battlefield.player1Side, 'frontLine');
    if (p1Front) {
      return {
        ...state,
        battlefield: {
          ...state.battlefield,
          player1Side: { ...state.battlefield.player1Side, frontLine: p1Front },
        },
      };
    }
    const p1Back = buffUnit(state.battlefield.player1Side, 'backLine');
    if (p1Back) {
      return {
        ...state,
        battlefield: {
          ...state.battlefield,
          player1Side: { ...state.battlefield.player1Side, backLine: p1Back },
        },
      };
    }

    // 嘗試 player2Side
    const p2Front = buffUnit(state.battlefield.player2Side, 'frontLine');
    if (p2Front) {
      return {
        ...state,
        battlefield: {
          ...state.battlefield,
          player2Side: { ...state.battlefield.player2Side, frontLine: p2Front },
        },
      };
    }
    const p2Back = buffUnit(state.battlefield.player2Side, 'backLine');
    if (p2Back) {
      return {
        ...state,
        battlefield: {
          ...state.battlefield,
          player2Side: { ...state.battlefield.player2Side, backLine: p2Back },
        },
      };
    }

    return state;
  }
}

// 已移除 RewardEffectHandler - 使用 RewardEffectHandlerV2
// 已移除 DrawEffectHandler - 使用 DrawEffectHandlerV2

// 匯出單例
export const effectTriggerSystem = new EffectTriggerSystem();
